import { BarChart3Icon } from "lucide-react";
import { InfoHint } from "@dolfin/ui";
import { AnalyticsFilterBar } from "@/components/analytics/AnalyticsFilterBar";
import { AnalyticsOrganisationsTable } from "@/components/analytics/AnalyticsOrganisationsTable";
import { CsvDownloadButton } from "@/components/analytics/CsvDownloadButton";
import { DOCUMENT_VOLUME_INFO_HINT, DocumentVolumeChart } from "@/components/analytics/DocumentVolumeChart";
import { InboxRoutingPanel } from "@/components/analytics/InboxRoutingPanel";
import { MatchGroupTimingPanel } from "@/components/analytics/MatchGroupTimingPanel";
import { MatchTimeOfDayChart } from "@/components/analytics/MatchTimeOfDayChart";
import { MatchOutcomesDailyChart } from "@/components/analytics/MatchOutcomesDailyChart";
import { useAnalyticsParams, type MatchUnit } from "@/components/analytics/useAnalyticsParams";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface AnalyticsSection {
  key: string;
  title: string;
  description: string;
  path: string;
  /** Whether this endpoint takes the Lines/Bills Unit parameter. */
  unitScoped: boolean;
  /** Always-visible InfoHint beside the title, for numbers that need context. */
  titleHint?: string;
  /** When set, renders the real chart instead of the "coming in a later task" placeholder. */
  renderBody?: (props: { apiParams: URLSearchParams; unit: MatchUnit }) => React.ReactNode;
}

const SECTIONS: AnalyticsSection[] = [
  {
    key: "match-timestamps",
    title: "Matches by time of day",
    description: "When matches happened through the day, by hour, in UTC.",
    path: "/admin/analytics/match-events",
    unitScoped: true,
    renderBody: ({ apiParams, unit }) => <MatchTimeOfDayChart apiParams={apiParams} unit={unit} />,
  },
  {
    key: "daily-outcomes",
    title: "Daily match outcomes",
    description: "How matches were made each day: automatically, from an approved suggestion, or by hand.",
    path: "/admin/analytics/match-outcomes-daily",
    unitScoped: true,
    renderBody: ({ apiParams, unit }) => <MatchOutcomesDailyChart apiParams={apiParams} unit={unit} />,
  },
  {
    key: "document-volume",
    title: "Document volume",
    description: "How many bills, purchase orders and delivery notes arrived each day.",
    path: "/admin/analytics/document-volume-daily",
    unitScoped: false,
    titleHint: DOCUMENT_VOLUME_INFO_HINT,
    renderBody: ({ apiParams }) => <DocumentVolumeChart apiParams={apiParams} />,
  },
  {
    key: "organisations",
    title: "Organisations",
    description:
      "Every organisation under the selected clients, with how many documents each one received in the selected date range.",
    path: "/admin/analytics/organisations",
    unitScoped: false,
    renderBody: ({ apiParams }) => <AnalyticsOrganisationsTable apiParams={apiParams} />,
  },
  {
    key: "inbox-routing",
    title: "Inbox routing",
    description:
      "How bills that arrived in a company inbox were assigned to an organisation, automatic versus manual.",
    path: "/admin/analytics/inbox-routing",
    unitScoped: false,
    renderBody: ({ apiParams }) => <InboxRoutingPanel apiParams={apiParams} />,
  },
  {
    key: "match-group-timing",
    title: "Time to first match",
    description:
      "How long match groups waited between being created and their first match, and how that first match was made.",
    path: "/admin/analytics/match-group-timing",
    unitScoped: false,
    renderBody: ({ apiParams }) => <MatchGroupTimingPanel apiParams={apiParams} />,
  },
];

function AnalyticsSectionCard({
  section,
  apiParams,
  unit,
}: {
  section: AnalyticsSection;
  apiParams: URLSearchParams;
  unit: MatchUnit;
}) {
  const params = new URLSearchParams(apiParams);
  if (section.unitScoped) params.set("Unit", unit);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-1.5">
          {section.title}
          {section.titleHint && <InfoHint label={section.title}>{section.titleHint}</InfoHint>}
        </CardTitle>
        <CardDescription>{section.description}</CardDescription>
        <CardAction>
          <CsvDownloadButton path={section.path} params={params} />
        </CardAction>
      </CardHeader>
      <CardContent>
        {section.renderBody ? (
          section.renderBody({ apiParams, unit })
        ) : (
          <div className="text-muted-foreground flex h-32 items-center justify-center rounded-md border border-dashed text-sm">
            Chart coming in a later task.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ThreeWayMatchPage() {
  const { apiParams, unit, ready } = useAnalyticsParams();

  return (
    <div>
      <AnalyticsFilterBar />
      <div className="mx-auto w-full max-w-[100rem] space-y-6 p-8">
        {!ready ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <BarChart3Icon className="text-muted-foreground size-8" />
              <p className="text-muted-foreground text-sm">Choose a client to load 3-way match analytics.</p>
            </CardContent>
          </Card>
        ) : (
          SECTIONS.map((section) => (
            <AnalyticsSectionCard
              key={section.key}
              section={section}
              apiParams={apiParams}
              unit={unit}
            />
          ))
        )}
      </div>
    </div>
  );
}
